// Resolves a unit's ability cast or basic auto-attack.
// All hits apply synchronously in the turn-based system (delayMs is ignored).

import { physicalDamage, magicDamage } from "./DamageCalc";
import { getAbility } from "../units/abilities";
import { effectiveStats } from "../units/UnitStats";
import { ITEMS } from "../data/items";
import { hexDistance } from "../engine/hex/HexCoord";
import type { Unit } from "../units/Unit";
import type { CombatContext } from "./CombatContext";
import type { EventBus } from "../engine/event/EventBus";
import type { CombatEvents } from "./events";
import type { RNG } from "../game/RNG";
import type { AbilityHit, StatusEffect } from "../units/abilities/Ability";

export function applyHit(
  source: Unit,
  hit: AbilityHit,
  bus: EventBus<CombatEvents>,
  damageMult = 1,
): void {
  if (hit.effect?.kind === "teleport") return;
  if (hit.target.currentHp <= 0 && hit.effect?.kind !== "shield") return;

  if (hit.amount > 0) {
    hit = { ...hit, amount: Math.round(hit.amount * damageMult) };
    // Banshee's Veil: block the first enemy ability hit
    if (
      hit.target.bansheeActive &&
      source.team !== hit.target.team &&
      hit.type !== "none"
    ) {
      hit.target.bansheeActive = false;
      return;
    }

    applyDamage(source, hit.target, hit.amount, hit.type as "physical" | "magical", bus, hit.suppressPopup, hit.orbPass, hit.alphaSlice, hit.jarvanPass, hit.ezrealPass, hit.tentacleHit, undefined, hit.hookPass, hit.katarinaSlice, hit.requiemBeam, hit.luxBeam, hit.mundoLightning, hit.chogathRupture, hit.kaisaRocket, hit.sivirBounce, hit.dravenAxe, hit.jinxRocket, hit.nidaleeSpear, hit.nidaleeBite, hit.neekoBounce, hit.viDash, hit.azirDash, hit.nasusAxe, hit.jaxSlam);

    // Executioner's Calling: apply anti-heal to the target on ability hits
    if (source.item === "executioners" && source.team !== hit.target.team) {
      hit.target.healReductionPercent = ITEMS.executioners.antiHealOnHit!;
      hit.target.healReductionTurnsRemaining = 2;
    }

    // Vampiric Scepter: omnivamp — heal source for 20% of damage dealt
    if (source.item === "vampiricScepter" && source.currentHp > 0) {
      const vampHeal = Math.round(hit.amount * ITEMS.vampiricScepter.omnivamp! * (1 - source.healReductionPercent));
      if (vampHeal > 0) {
        const maxHp = effectiveStats(source).maxHp;
        source.currentHp = Math.min(maxHp, source.currentHp + vampHeal);
        bus.emit("unit.healed", { unit: source, amount: vampHeal });
      }
    }
  }

  if (hit.heal && hit.heal > 0) {
    const reduction = hit.target.healReductionPercent;
    const actualHeal = reduction > 0 ? Math.round(hit.heal * (1 - reduction)) : hit.heal;
    if (actualHeal > 0) {
      const maxHp = effectiveStats(hit.target).maxHp;
      hit.target.currentHp = Math.min(maxHp, hit.target.currentHp + actualHeal);
      bus.emit("unit.healed", { unit: hit.target, amount: actualHeal });
    }
  }

  if (hit.cdrTurns && hit.cdrTurns > 0 && hit.target.cooldownTurns > 0) {
    hit.target.cooldownTurns = Math.max(0, hit.target.cooldownTurns - hit.cdrTurns);
  }

  if (hit.effect) {
    applyStatusEffect(hit.target, hit.effect, bus);
  }
}

export function resolveCast(
  attacker: Unit,
  target: Unit,
  ctx: CombatContext,
  bus: EventBus<CombatEvents>,
  _rng: RNG,
  doubleCast = false,
): void {
  if (attacker.currentHp <= 0 || target.currentHp <= 0) return;
  if (!attacker.def.abilityId) return;

  const ability = getAbility(attacker.def.abilityId);
  const shape = ability.targetShape({ caster: attacker, target, combat: ctx });

  const hits = ability.computeHits({ caster: attacker, target, combat: ctx });

  // Fire teleports before emitting the casted event so the animator sees updated positions.
  for (const hit of hits) {
    if (hit.effect?.kind === "teleport") ctx.teleportUnit(hit.target, hit.effect.hex);
  }

  const abilityTarget = hits.find(h => h.amount > 0)?.target ?? hits.find(h => (h.heal ?? 0) > 0)?.target ?? target;
  bus.emit("unit.casted", { caster: attacker, target: abilityTarget, abilityId: ability.id, shape, doubleCast });

  const deferred = hits.filter(h => h.deferredSpin);
  if (deferred.length > 0) ctx.deferSpinHits?.(attacker, deferred);

  let chainKilled = false;
  for (let hit of hits) {
    if (hit.effect?.kind === "teleport") continue;
    if (hit.deferredSpin) continue;
    if (hit.chainStop && chainKilled) continue;
    if (hit.retargetIfDead && hit.target.currentHp <= 0) {
      const enemyTeam = attacker.team === "player" ? "enemy" : "player";
      const candidates = ctx.livingUnitsOf(enemyTeam);
      if (candidates.length === 0) continue;
      candidates.sort((a, b) => hexDistance(attacker.pos, a.pos) - hexDistance(attacker.pos, b.pos));
      hit = { ...hit, target: candidates[0] };
    }
    const hpBefore = hit.target.currentHp;
    applyHit(attacker, hit, bus, ctx.damageMult);
    if (hit.chainStop && hpBefore > 0 && hit.target.currentHp <= 0) {
      chainKilled = true;
    }
  }
}

function applyDamage(
  source: Unit,
  target: Unit,
  amount: number,
  type: "physical" | "magical",
  bus: EventBus<CombatEvents>,
  suppressPopup?: boolean,
  orbPass?: "out" | "back",
  alphaSlice?: boolean,
  jarvanPass?: boolean,
  ezrealPass?: boolean,
  tentacleHit?: boolean,
  caitlynHeadshot?: boolean,
  hookPass?: boolean,
  katarinaSlice?: boolean,
  requiemBeam?: boolean,
  luxBeam?: boolean,
  mundoLightning?: boolean,
  chogathRupture?: boolean,
  kaisaRocket?: boolean,
  sivirBounce?: boolean,
  dravenAxe?: boolean,
  jinxRocket?: boolean,
  nidaleeSpear?: boolean,
  nidaleeBite?: boolean,
  neekoBounce?: boolean,
  viDash?: boolean,
  azirDash?: boolean,
  nasusAxe?: boolean,
  jaxSlam?: boolean,
): void {
  if (target.alphaStrikeTurnsRemaining > 0) return;

  // Wake a sleeping unit on any incoming damage
  if (target.sleepTurnsRemaining > 0) target.sleepTurnsRemaining = 0;

  if (target.damageReductionPercent > 0) {
    amount = Math.round(amount * (1 - target.damageReductionPercent));
  }

  source.damageDealt += amount;

  if (target.shieldAmount > 0) {
    const absorbed = Math.min(target.shieldAmount, amount);
    target.shieldAmount -= absorbed;
    amount -= absorbed;
    if (amount <= 0) return;
  }

  target.currentHp = Math.max(0, target.currentHp - amount);
  bus.emit("damage.dealt", { source, target, amount, type, remainingHp: target.currentHp, suppressPopup, orbPass, alphaSlice, jarvanPass, ezrealPass, tentacleHit, caitlynHeadshot, hookPass, katarinaSlice, requiemBeam, luxBeam, mundoLightning, chogathRupture, kaisaRocket, sivirBounce, dravenAxe, jinxRocket, nidaleeSpear, nidaleeBite, neekoBounce, viDash, azirDash, nasusAxe, jaxSlam });

  // Red buff omnivamp: heal 15% of any damage dealt (autos, abilities, passives)
  if (source.redBuff && source !== target && source.currentHp > 0 && amount > 0) {
    const heal = Math.round(amount * 0.15 * (1 - source.healReductionPercent));
    if (heal > 0) {
      const maxHp = effectiveStats(source).maxHp;
      source.currentHp = Math.min(maxHp, source.currentHp + heal);
      bus.emit("unit.healed", { unit: source, amount: heal });
    }
  }

  if (target.currentHp <= 0) {
    bus.emit("unit.died", { unit: target });
  }
}

function applyStatusEffect(
  target: Unit,
  effect: StatusEffect,
  bus: EventBus<CombatEvents>,
): void {
  switch (effect.kind) {
    case "stun":
      if (target.olafUltTurnsRemaining > 0) {
        bus.emit("unit.statusApplied", { unit: target, effect: { kind: "olafCcImmune" } });
        return;
      }
      target.stunTurnsRemaining += effect.duration;
      break;
    case "stasis":
      if (target.olafUltTurnsRemaining > 0) {
        bus.emit("unit.statusApplied", { unit: target, effect: { kind: "olafCcImmune" } });
        return;
      }
      target.stasisTurnsRemaining += effect.duration;
      break;
    case "sleep":
      if (target.olafUltTurnsRemaining > 0) {
        bus.emit("unit.statusApplied", { unit: target, effect: { kind: "olafCcImmune" } });
        return;
      }
      target.sleepTurnsRemaining = Math.max(target.sleepTurnsRemaining, effect.duration);
      break;
    case "bleed":
      target.bleedTickDamage = Math.max(target.bleedTickDamage, effect.tickDamage);
      target.bleedTurnsRemaining = Math.max(target.bleedTurnsRemaining, effect.duration);
      target.passiveStacks += 1;
      break;
    case "shield":
      target.shieldAmount += effect.amount;
      break;
    case "teleport":
      return;
    case "damageReduction":
      target.damageReductionPercent = effect.percent;
      target.damageReductionTurnsRemaining = effect.duration;
      break;
    case "olafUlt":
      target.olafUltTurnsRemaining = effect.duration;
      target.stunTurnsRemaining = 0;
      target.stasisTurnsRemaining = 0;
      break;
    case "alphaStrike":
      target.alphaStrikeTurnsRemaining = effect.duration;
      break;
    case "jaxCounterStrike":
      target.jaxCounterStrikeTurnsRemaining = effect.duration;
      target.jaxCounterStrikeBlockedCount = 0;
      break;
  }
  bus.emit("unit.statusApplied", { unit: target, effect });
}

export function resolveAutoAttack(
  attacker: Unit,
  target: Unit,
  bus: EventBus<CombatEvents>,
  damageMult = 1,
): void {
  if (attacker.currentHp <= 0 || target.currentHp <= 0) return;

  const attackerStats = effectiveStats(attacker);
  attacker.autoAttackCount += 1;

  const bonusEvery = attacker.championDef?.bonusAutoEvery;
  const isBonus = bonusEvery !== undefined && attacker.autoAttackCount % bonusEvery === 0;
  const critChance = attacker.def.critChance ?? 0;
  const isCrit = critChance > 0 && Math.random() < critChance;
  const baseMultiplier = isBonus ? 1.5 : isCrit ? 1.75 : 1;
  let dmg = physicalDamage(attackerStats.ad * baseMultiplier);

  // Randuin's Omen: reduce incoming auto attack damage by 35%
  if (target.item === "randuins") {
    dmg = Math.round(dmg * (1 - ITEMS.randuins.randuinReduction!));
  }

  bus.emit("unit.attacked", { attacker, target, doubleStrike: false, isEmpowered: isBonus || isCrit });

  if (target.jaxCounterStrikeTurnsRemaining > 0) {
    target.jaxCounterStrikeBlockedCount += 1;
    bus.emit("unit.dodged", { unit: target, attacker });
    return;
  }

  applyDamage(attacker, target, Math.round(dmg * damageMult), "physical", bus);

  // Darius passive: each auto-attack applies a bleed stack
  if (attacker.def.abilityId === "dariusApprehend" && target.currentHp > 0) {
    target.bleedTickDamage = Math.max(target.bleedTickDamage, 35);
    target.bleedTurnsRemaining = Math.max(target.bleedTurnsRemaining, 3);
    target.passiveStacks += 1;
    bus.emit("unit.statusApplied", { unit: target, effect: { kind: "bleed", tickDamage: 35, duration: 3 } });
  }

  // Vayne passive: Silver Bolts — every 3 stacks deals 35% max-HP bonus damage.
  // Stacks added per auto scale with stars: 1★ +1, 2★ +2, 3★ +3 (so 1★ procs every 3rd auto,
  // 2★ every 2nd, 3★ every auto). The animator stages the ring appearances with a small
  // delay between each so the build-up reads visually.
  if (attacker.championDef?.id === "Vayne" && target.currentHp > 0) {
    const stacksToAdd = Math.max(1, attacker.stars);
    let added = 0;
    let procced = false;
    for (let i = 0; i < stacksToAdd; i++) {
      target.vayneStacks += 1;
      added += 1;
      if (target.vayneStacks >= 3) {
        const bonusDmg = Math.round(effectiveStats(target).maxHp * 0.35);
        applyDamage(attacker, target, bonusDmg, "physical", bus);
        target.vayneStacks = 0;
        procced = true;
        break;
      }
    }
    bus.emit("vayne.silverBolts", { attacker, target, addedStacks: added, procced });
  }

  // Executioner's Calling: apply anti-heal debuff on hit
  if (attacker.item === "executioners") {
    target.healReductionPercent = ITEMS.executioners.antiHealOnHit!;
    target.healReductionTurnsRemaining = 2;
  }

  const baseLifesteal = attacker.championDef?.lifestealPercent ?? 0;
  const ultLifesteal = attacker.olafUltTurnsRemaining > 0 ? 0.18 : 0;
  const lifesteal = baseLifesteal + ultLifesteal;
  if (lifesteal > 0 && attacker.currentHp > 0) {
    const healMult = 1 - attacker.healReductionPercent;
    const heal = Math.round(dmg * lifesteal * healMult);
    if (heal > 0) {
      const maxHp = effectiveStats(attacker).maxHp;
      attacker.currentHp = Math.min(maxHp, attacker.currentHp + heal);
      bus.emit("unit.healed", { unit: attacker, amount: heal });
    }
  }

  // Vampiric Scepter: omnivamp — heal attacker for 20% of damage dealt
  if (attacker.item === "vampiricScepter" && attacker.currentHp > 0) {
    const vampHeal = Math.round(dmg * ITEMS.vampiricScepter.omnivamp! * (1 - attacker.healReductionPercent));
    if (vampHeal > 0) {
      const maxHp = effectiveStats(attacker).maxHp;
      attacker.currentHp = Math.min(maxHp, attacker.currentHp + vampHeal);
      bus.emit("unit.healed", { unit: attacker, amount: vampHeal });
    }
  }
}

export { physicalDamage, magicDamage };
